<template>
    <div
        :class="[
            getStyles,
            borderStyle,
            backgroundStyle,
            { 'shadow-muval-5': !inline },
            { 'items-center': !triggerText },
            $slots.action ? 'py-2 pr-2' : 'py-0.5',
        ]"
        class="rounded-muval-1 relative container flex gap-2 border-l-4 pl-2 antialiased"
    >
        <!-- The icon is pinned to the first text line, so its offset tracks the banner's top padding -->
        <div
            :class="$slots.action ? 'top-3.5' : 'top-2'"
            class="absolute left-2.5 w-fit"
        >
            <SvgIcon
                :name="getIcon"
                class="h-5 w-5"
            />
        </div>

        <p
            v-if="!triggerText"
            class="text-muval-gray-1 container py-1.5 pr-4 pl-8 text-sm font-normal"
        >
            <slot />
        </p>

        <CollapsibleRoot
            v-else
            v-model:open="isOpen"
            :default-open="openByDefault"
            class="container pr-4 pl-8"
        >
            <CollapsibleTrigger
                class="text-muval-gray-1 flex cursor-pointer items-center gap-1.5 py-1.5 text-sm font-medium"
            >
                <span>{{ triggerText }}</span>
                <SvgIcon
                    name="system-chevron-down"
                    class="size-4 transition-transform duration-150"
                    :class="{ '-rotate-90': !isOpen }"
                />
            </CollapsibleTrigger>
            <CollapsibleContent class="CollapsibleContent">
                <p class="text-muval-gray-1 pb-1.5 text-sm font-normal">
                    <slot />
                </p>
            </CollapsibleContent>
        </CollapsibleRoot>

        <div
            v-if="$slots.action"
            class="shrink-0"
        >
            <slot name="action" />
        </div>
    </div>
</template>
<script setup>
const props = defineProps({
    type: {
        type: String,
        required: false,
        default: 'danger',
        validator: (s) => ['info', 'alert', 'warning'].includes(s),
    },
    inline: {
        type: Boolean,
        required: false,
        default: false,
    },
    icon: {
        type: [String, null],
        required: false,
        default: null,
    },
    triggerText: {
        type: String,
        required: false,
        default: null,
    },
    openByDefault: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const isOpen = ref(props.openByDefault);

const getIcon = computed(() => {
    if (props.icon) {
        return props.icon;
    }
    switch (props.type) {
        case 'warning':
            return 'system-warning';
        case 'alert':
            return 'system-information-filled';
        default:
            return 'system-information-filled';
    }
});

const getStyles = computed(() => {
    if (props.inline) {
        switch (props.type) {
            case 'info':
                return 'text-muval-information';
            case 'alert':
                return 'text-muval-amber';
            default:
                return 'text-error';
        }
    } else {
        switch (props.type) {
            case 'info':
                return 'text-muval-information ';
            case 'alert':
                return 'text-muval-amber ';
            default:
                return 'text-error ';
        }
    }
});

const borderStyle = computed(() => {
    switch (props.type) {
        case 'info':
            return 'border-muval-teal';
        case 'alert':
            return 'border-muval-amber';
        default:
            return 'border-error';
    }
});

const backgroundStyle = computed(() => {
    // if (!props.inline) {
    //     return 'bg-white';
    // }
    switch (props.type) {
        case 'info':
            return 'bg-muval-teal/10';
        case 'alert':
            return 'bg-muval-amber/10';
        default:
            return 'bg-muval-red-2';
    }
});
</script>
<style scoped>
.CollapsibleContent {
    overflow: hidden;
}
.CollapsibleContent[data-state='open'] {
    animation: slideDown 150ms ease-in-out;
}
.CollapsibleContent[data-state='closed'] {
    animation: slideUp 150ms ease-in-out;
}

@keyframes slideDown {
    from {
        height: 0;
    }
    to {
        height: var(--radix-collapsible-content-height);
    }
}

@keyframes slideUp {
    from {
        height: var(--radix-collapsible-content-height);
    }
    to {
        height: 0;
    }
}
</style>
